import React, { useState, useEffect } from 'react';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis,
    CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Import pricing data
import GPUHardware from './data/DeploymentPricing/gpu-hardware.json';
import CloudRental from './data/DeploymentPricing/cloud-rental.json';
import TokenPricing from './data/DeploymentPricing/updated-token-pricing.json';
import ProvisionedThroughput from './data/DeploymentPricing/provisioned-throughput.json';

// Import utilities
import { formatCurrency, calculateSessionTokens, calculateRequiredThroughput } from './utils/deployment/deploymentHelpers';
import { DEFAULT_VALUES, CHART_COLORS, PROJECTION_YEARS } from './utils/deployment/deploymentConstants';
import {
    calculatePurchasedHardwareCost,
    calculateCloudRentalCost,
    calculateTokenUsageCost,
    calculateProvisionedThroughputCost,
    calculateHybridCost,
    getBestOption
} from './utils/deployment/costCalculations';

// Import components
import ParameterSliders from './components/deployment/ParameterSliders';

const DeploymentCostCalculator = () => {
    // Input parameters (with localStorage persistence)
    const [sessionsPerMonth, setSessionsPerMonth] = useState(() => {
        const saved = localStorage.getItem('sessionsPerMonth');
        return saved ? parseInt(saved) : DEFAULT_VALUES.sessionsPerMonth;
    });
    const [operationsHoursPerDay, setOperationsHoursPerDay] = useState(() => {
        const saved = localStorage.getItem('operationsHoursPerDay');
        return saved ? parseInt(saved) : DEFAULT_VALUES.operationsHoursPerDay;
    });
    const [tokensPerRequest, setTokensPerRequest] = useState(() => {
        const saved = localStorage.getItem('tokensPerRequest');
        return saved ? parseInt(saved) : DEFAULT_VALUES.tokensPerRequest;
    });
    const [tokensInResponse, setTokensInResponse] = useState(() => {
        const saved = localStorage.getItem('tokensInResponse');
        return saved ? parseInt(saved) : DEFAULT_VALUES.tokensInResponse;
    });
    const [requestsPerSession, setRequestsPerSession] = useState(() => {
        const saved = localStorage.getItem('requestsPerSession');
        return saved ? parseInt(saved) : DEFAULT_VALUES.requestsPerSession;
    });
    const [percentResponseInSubsequent, setPercentResponseInSubsequent] = useState(() => {
        const saved = localStorage.getItem('percentResponseInSubsequent');
        return saved ? parseInt(saved) : DEFAULT_VALUES.percentResponseInSubsequent;
    });
    const [yoyGrowthPercent, setYoyGrowthPercent] = useState(() => {
        const saved = localStorage.getItem('yoyGrowthPercent');
        return saved ? parseInt(saved) : DEFAULT_VALUES.yoyGrowthPercent;
    });

    // Position state for Year 1 Requirements floating div (with localStorage)
    const [reqPosition, setReqPosition] = useState(() => {
        const saved = localStorage.getItem('year1ReqPosition');
        return saved ? JSON.parse(saved) : { x: 0, y: 250 };
    });
    const [isReqDragging, setIsReqDragging] = useState(false);
    const [reqDragStart, setReqDragStart] = useState({ x: 0, y: 0 });

    // Save Year 1 Requirements position to localStorage
    useEffect(() => {
        localStorage.setItem('year1ReqPosition', JSON.stringify(reqPosition));
    }, [reqPosition]);

    // Save parameter values to localStorage
    useEffect(() => {
        localStorage.setItem('sessionsPerMonth', sessionsPerMonth.toString());
    }, [sessionsPerMonth]);
    useEffect(() => {
        localStorage.setItem('operationsHoursPerDay', operationsHoursPerDay.toString());
    }, [operationsHoursPerDay]);
    useEffect(() => {
        localStorage.setItem('tokensPerRequest', tokensPerRequest.toString());
    }, [tokensPerRequest]);
    useEffect(() => {
        localStorage.setItem('tokensInResponse', tokensInResponse.toString());
    }, [tokensInResponse]);
    useEffect(() => {
        localStorage.setItem('requestsPerSession', requestsPerSession.toString());
    }, [requestsPerSession]);
    useEffect(() => {
        localStorage.setItem('percentResponseInSubsequent', percentResponseInSubsequent.toString());
    }, [percentResponseInSubsequent]);
    useEffect(() => {
        localStorage.setItem('yoyGrowthPercent', yoyGrowthPercent.toString());
    }, [yoyGrowthPercent]);

    // Deployment options (with localStorage)
    const [selectedHardware, setSelectedHardware] = useState(() => {
        const saved = localStorage.getItem('selectedHardware');
        return saved ? JSON.parse(saved) : GPUHardware[1];
    });
    const [selectedCloudRental, setSelectedCloudRental] = useState(() => {
        const saved = localStorage.getItem('selectedCloudRental');
        return saved ? JSON.parse(saved) : CloudRental[5];
    });
    const [selectedTokenModel, setSelectedTokenModel] = useState(() => {
        const saved = localStorage.getItem('selectedTokenModel');
        return saved ? JSON.parse(saved) : TokenPricing[2];
    });
    const [selectedPTModel, setSelectedPTModel] = useState(() => {
        const saved = localStorage.getItem('selectedPTModel');
        return saved ? JSON.parse(saved) : ProvisionedThroughput[0];
    });
    const [selectedPTCommitment, setSelectedPTCommitment] = useState(() => {
        const saved = localStorage.getItem('selectedPTCommitment');
        return saved || '12 Months';
    });

    // Save deployment selections to localStorage
    useEffect(() => {
        localStorage.setItem('selectedHardware', JSON.stringify(selectedHardware));
    }, [selectedHardware]);
    useEffect(() => {
        localStorage.setItem('selectedCloudRental', JSON.stringify(selectedCloudRental));
    }, [selectedCloudRental]);
    useEffect(() => {
        localStorage.setItem('selectedTokenModel', JSON.stringify(selectedTokenModel));
    }, [selectedTokenModel]);
    useEffect(() => {
        localStorage.setItem('selectedPTModel', JSON.stringify(selectedPTModel));
    }, [selectedPTModel]);
    useEffect(() => {
        localStorage.setItem('selectedPTCommitment', selectedPTCommitment);
    }, [selectedPTCommitment]);

    // Hybrid configuration
    const [hybridOnPremPercent, setHybridOnPremPercent] = useState(() => {
        const saved = localStorage.getItem('hybridOnPremPercent');
        return saved ? Number(saved) : DEFAULT_VALUES.hybridOnPremPercent;
    });
    const [hybridCloudOption, setHybridCloudOption] = useState(() => {
        const saved = localStorage.getItem('hybridCloudOption');
        return saved || 'rental';
    });

    // Save hybrid configuration to localStorage
    useEffect(() => {
        localStorage.setItem('hybridOnPremPercent', hybridOnPremPercent.toString());
    }, [hybridOnPremPercent]);
    useEffect(() => {
        localStorage.setItem('hybridCloudOption', hybridCloudOption);
    }, [hybridCloudOption]);

    // Calculated values
    const [totalInputTokensPerSession, setTotalInputTokensPerSession] = useState(0);
    const [totalOutputTokensPerSession, setTotalOutputTokensPerSession] = useState(0);
    const [requiredThroughput, setRequiredThroughput] = useState(0);

    // Calculate derived values whenever inputs change
    useEffect(() => {
        const { totalInputTokens, totalOutputTokens } = calculateSessionTokens({
            requestsPerSession,
            tokensPerRequest,
            tokensInResponse,
            percentResponseInSubsequent
        });

        setTotalInputTokensPerSession(totalInputTokens);
        setTotalOutputTokensPerSession(totalOutputTokens);

        const throughput = calculateRequiredThroughput({
            totalTokensPerSession: totalInputTokens + totalOutputTokens,
            sessionsPerMonth,
            operationsHoursPerDay
        });

        setRequiredThroughput(throughput);
    }, [sessionsPerMonth, operationsHoursPerDay, tokensPerRequest, tokensInResponse, requestsPerSession, percentResponseInSubsequent]);

    // Calculate costs for all deployment options
    const commonParams = {
        sessionsPerMonth,
        operationsHoursPerDay,
        totalInputTokensPerSession,
        totalOutputTokensPerSession,
        requiredThroughput,
        yoyGrowthPercent
    };

    const hardwareCost = calculatePurchasedHardwareCost({
        ...commonParams,
        selectedHardware
    });

    const rentalCost = calculateCloudRentalCost({
        ...commonParams,
        selectedCloudRental
    });

    const tokenCost = calculateTokenUsageCost({
        ...commonParams,
        selectedTokenModel
    });

    const ptCost = calculateProvisionedThroughputCost({
        ...commonParams,
        selectedPTModel,
        selectedPTCommitment
    });

    const hybridCost = calculateHybridCost({
        ...commonParams,
        hybridOnPremPercent,
        hybridCloudOption,
        selectedHardware,
        selectedCloudRental,
        selectedTokenModel,
        selectedPTModel,
        selectedPTCommitment
    });

    const bestOption = getBestOption({
        hardwareCost,
        rentalCost,
        tokenCost,
        ptCost,
        hybridCost,
        enableHybrid: true
    });

    // Prepare chart data (5 years)
    const yearlyComparisonData = [];
    for (let year = 1; year <= PROJECTION_YEARS; year++) {
        const yearData = {
            year: `Y${year}`,
            'Hardware': hardwareCost[`year${year}`],
            'Rental': rentalCost[`year${year}`],
            'Tokens': tokenCost[`year${year}`],
            'PT': ptCost[`year${year}`],
            'Hybrid': hybridCost[`year${year}`]
        };
        yearlyComparisonData.push(yearData);
    }

    // Calculate cumulative costs
    const cumulativeComparisonData = yearlyComparisonData.map((item, index) => {
        const cumulative = { year: item.year };
        Object.keys(item).forEach(key => {
            if (key !== 'year') {
                cumulative[key] = yearlyComparisonData
                    .slice(0, index + 1)
                    .reduce((sum, y) => sum + (y[key] || 0), 0);
            }
        });
        return cumulative;
    });

    // Year 1 Requirements drag handlers
    const handleReqMouseDown = (e) => {
        setIsReqDragging(true);
        // Store the offset between mouse and div's current position
        setReqDragStart({
            x: e.clientX - reqPosition.x,
            y: e.clientY - reqPosition.y
        });
    };

    const handleReqMouseMove = (e) => {
        if (isReqDragging) {
            // Calculate new position: mouse position minus the offset we stored
            const newX = e.clientX - reqDragStart.x;
            const newY = e.clientY - reqDragStart.y;
            setReqPosition({
                x: newX,
                y: newY
            });
        }
    };

    const handleReqMouseUp = () => {
        setIsReqDragging(false);
        console.log('Year 1 Requirements Position:', reqPosition);
    };

    useEffect(() => {
        if (isReqDragging) {
            window.addEventListener('mousemove', handleReqMouseMove);
            window.addEventListener('mouseup', handleReqMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleReqMouseMove);
                window.removeEventListener('mouseup', handleReqMouseUp);
            };
        }
    }, [isReqDragging, reqDragStart]);

    return (
        <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
            {/* FIXED HEADER - Non-scrollable */}
            <div className="flex-none bg-white border-b-2 border-gray-300 shadow-lg">
                {/* Title */}
                <div className="text-center py-2 px-4 bg-gradient-to-r from-blue-500 to-indigo-600">
                    <h2 className="text-xl font-bold text-white">AI Deployment Cost Calculator</h2>
                </div>

                {/* Recommendation - Always Visible */}
                <div className="px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">🎯</span>
                            <div>
                                <div className="text-xs text-gray-600 font-semibold mb-0.5">Recommended Deployment:</div>
                                <span className="font-bold text-lg text-green-700">{bestOption.name}</span>
                                <span className="text-sm text-gray-600 ml-2">({bestOption.type})</span>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-green-700">{formatCurrency(bestOption.cost)}</div>
                            <div className="text-xs text-gray-600">5-Year Total{yoyGrowthPercent > 0 && ` @ ${yoyGrowthPercent}% YoY`}</div>
                        </div>
                    </div>
                </div>

                {/* Cost Cards Row - Always Visible */}
                <div className="px-4 py-2 bg-white border-b">
                    <div className="flex gap-2">
                        <div className={`flex-1 p-2 rounded text-center border-2 ${bestOption.name === 'Purchased Hardware' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="text-xs font-bold">💻 Hardware</div>
                            <div className="text-sm font-bold" style={{color: '#8884d8'}}>{formatCurrency(hardwareCost.fiveYearTotal)}</div>
                        </div>
                        <div className={`flex-1 p-2 rounded text-center border-2 ${bestOption.name === 'Cloud Rental' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="text-xs font-bold">☁️ Rental</div>
                            <div className="text-sm font-bold" style={{color: '#82ca9d'}}>{formatCurrency(rentalCost.fiveYearTotal)}</div>
                        </div>
                        <div className={`flex-1 p-2 rounded text-center border-2 ${bestOption.name === 'Token Usage' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="text-xs font-bold">🎯 Tokens</div>
                            <div className="text-sm font-bold" style={{color: '#ffc658'}}>{formatCurrency(tokenCost.fiveYearTotal)}</div>
                        </div>
                        <div className={`flex-1 p-2 rounded text-center border-2 ${bestOption.name === 'Provisioned Throughput' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="text-xs font-bold">⚡ PT</div>
                            <div className="text-sm font-bold" style={{color: '#ff69b4'}}>{formatCurrency(ptCost.fiveYearTotal)}</div>
                        </div>
                        <div className={`flex-1 p-2 rounded text-center border-2 ${bestOption.name === 'Hybrid Deployment' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div className="text-xs font-bold">🔄 Hybrid</div>
                            <div className="text-sm font-bold" style={{color: '#ff7c7c'}}>{formatCurrency(hybridCost.fiveYearTotal)}</div>
                        </div>
                    </div>
                </div>

                {/* Charts Row - Always Visible */}
                <div className="grid grid-cols-2 gap-3 px-4 py-2 bg-gray-50">
                    <div className="bg-white p-2 rounded shadow border">
                        <h3 className="text-xs font-semibold mb-1">Annual Costs</h3>
                        <ResponsiveContainer width="100%" height={150}>
                            <BarChart data={yearlyComparisonData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year" tick={{fontSize: 9}} />
                                <YAxis tickFormatter={formatCurrency} tick={{fontSize: 9}} width={50} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Bar dataKey="Hardware" fill="#8884d8" />
                                <Bar dataKey="Rental" fill="#82ca9d" />
                                <Bar dataKey="Tokens" fill="#ffc658" />
                                <Bar dataKey="PT" fill="#ff69b4" />
                                <Bar dataKey="Hybrid" fill="#ff7c7c" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white p-2 rounded shadow border">
                        <h3 className="text-xs font-semibold mb-1">Cumulative 5-Year</h3>
                        <ResponsiveContainer width="100%" height={150}>
                            <LineChart data={cumulativeComparisonData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year" tick={{fontSize: 9}} />
                                <YAxis tickFormatter={formatCurrency} tick={{fontSize: 9}} width={50} />
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Line type="monotone" dataKey="Hardware" stroke="#8884d8" strokeWidth={2} />
                                <Line type="monotone" dataKey="Rental" stroke="#82ca9d" strokeWidth={2} />
                                <Line type="monotone" dataKey="Tokens" stroke="#ffc658" strokeWidth={2} />
                                <Line type="monotone" dataKey="PT" stroke="#ff69b4" strokeWidth={2} />
                                <Line type="monotone" dataKey="Hybrid" stroke="#ff7c7c" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            {/* Floating Divs - Outside scrollable area */}
            <ParameterSliders
                sessionsPerMonth={sessionsPerMonth}
                setSessionsPerMonth={setSessionsPerMonth}
                operationsHoursPerDay={operationsHoursPerDay}
                setOperationsHoursPerDay={setOperationsHoursPerDay}
                tokensPerRequest={tokensPerRequest}
                setTokensPerRequest={setTokensPerRequest}
                tokensInResponse={tokensInResponse}
                setTokensInResponse={setTokensInResponse}
                requestsPerSession={requestsPerSession}
                setRequestsPerSession={setRequestsPerSession}
                percentResponseInSubsequent={percentResponseInSubsequent}
                setPercentResponseInSubsequent={setPercentResponseInSubsequent}
                yoyGrowthPercent={yoyGrowthPercent}
                setYoyGrowthPercent={setYoyGrowthPercent}
            />

            {/* Year 1 Requirements - Floating */}
            <div
                className="fixed p-3 bg-white rounded-lg border-2 border-green-300 shadow-lg z-50"
                style={{
                    left: `${reqPosition.x}px`,
                    top: `${reqPosition.y}px`,
                    cursor: isReqDragging ? 'grabbing' : 'grab',
                    width: '280px'
                }}
            >
                <div
                    className="bg-gray-200 px-3 py-2 -mx-3 -mt-3 mb-2 rounded-t-lg cursor-move border-b-2 border-gray-400"
                    onMouseDown={handleReqMouseDown}
                >
                    <h3 className="text-xs font-semibold text-gray-700 flex items-center">
                        <span className="mr-2">⋮⋮</span>
                        📈 Year 1 Requirements
                        <span className="ml-auto text-[10px] text-gray-500 italic">(drag to move)</span>
                    </h3>
                </div>
                <div className="space-y-1 text-xs">
                    <div className="flex items-center justify-between">
                        <span className="text-gray-700">Throughput:</span>
                        <span className="font-bold text-blue-600">
                            {requiredThroughput.toLocaleString(undefined, { maximumFractionDigits: 0 })} t/s
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-700">Input Tokens/Session:</span>
                        <span className="font-bold text-purple-600">
                            {totalInputTokensPerSession.toLocaleString()}
                        </span>
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-gray-700">Output Tokens/Session:</span>
                        <span className="font-bold text-orange-600">
                            {totalOutputTokensPerSession.toLocaleString()}
                        </span>
                    </div>
                </div>
            </div>

            {/* SCROLLABLE CONTENT - Deployment Options */}
            <div className="flex-1 overflow-y-auto p-4">

                {/* Deployment Options - Horizontal Table Layout */}
                <div className="bg-white rounded-lg border-2 border-gray-300 overflow-hidden">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="bg-gray-100 border-b">
                                <th className="text-left py-2 px-3 font-semibold">Type</th>
                                <th className="text-left py-2 px-3 font-semibold">Selection</th>
                                <th className="text-center py-2 px-2 font-semibold">Y1</th>
                                <th className="text-center py-2 px-2 font-semibold">Y2</th>
                                <th className="text-center py-2 px-2 font-semibold">Y3</th>
                                <th className="text-center py-2 px-2 font-semibold">Y4</th>
                                <th className="text-center py-2 px-2 font-semibold">Y5</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Hardware Option */}
                            <tr className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">💻 Purchased Hardware</div>
                                    <div className="text-[10px] text-gray-500">({hardwareCost.type})</div>
                                </td>
                                <td className="py-2 px-3">
                                    <select
                                        value={JSON.stringify(selectedHardware)}
                                        onChange={(e) => setSelectedHardware(JSON.parse(e.target.value))}
                                        className="w-full p-1 border rounded text-xs"
                                    >
                                        {GPUHardware.map(hw => (
                                            <option key={hw.name} value={JSON.stringify(hw)}>
                                                {hw.name} - {formatCurrency(hw.purchasePrice)}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hardwareCost.year1)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hardwareCost.year2)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hardwareCost.year3)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hardwareCost.year4)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hardwareCost.year5)}</td>
                            </tr>

                            {/* Cloud Rental Option */}
                            <tr className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">☁️ Cloud Rental</div>
                                    <div className="text-[10px] text-gray-500">({rentalCost.type})</div>
                                </td>
                                <td className="py-2 px-3">
                                    <select
                                        value={JSON.stringify(selectedCloudRental)}
                                        onChange={(e) => setSelectedCloudRental(JSON.parse(e.target.value))}
                                        className="w-full p-1 border rounded text-xs"
                                    >
                                        {CloudRental.sort((a, b) => a.name.localeCompare(b.name)).map(cloud => (
                                            <option key={cloud.name} value={JSON.stringify(cloud)}>
                                                {cloud.name} - ${cloud.hourlyRate}/hr
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(rentalCost.year1)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(rentalCost.year2)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(rentalCost.year3)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(rentalCost.year4)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(rentalCost.year5)}</td>
                            </tr>

                            {/* Token-Based Option */}
                            <tr className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">🎯 Token-Based</div>
                                    <div className="text-[10px] text-gray-500">({tokenCost.type})</div>
                                </td>
                                <td className="py-2 px-3">
                                    <select
                                        value={JSON.stringify(selectedTokenModel)}
                                        onChange={(e) => setSelectedTokenModel(JSON.parse(e.target.value))}
                                        className="w-full p-1 border rounded text-xs"
                                    >
                                        {TokenPricing.sort((a, b) => a.model.localeCompare(b.model)).map(model => (
                                            <option key={model.model} value={JSON.stringify(model)}>
                                                {model.model}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(tokenCost.year1)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(tokenCost.year2)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(tokenCost.year3)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(tokenCost.year4)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(tokenCost.year5)}</td>
                            </tr>

                            {/* Provisioned Throughput Option */}
                            <tr className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">⚡ Provisioned Throughput</div>
                                    <div className="text-[10px] text-gray-500">({ptCost.type})</div>
                                </td>
                                <td className="py-2 px-3">
                                    <select
                                        value={JSON.stringify(selectedPTModel)}
                                        onChange={(e) => setSelectedPTModel(JSON.parse(e.target.value))}
                                        className="w-full p-1 border rounded text-xs mb-1"
                                    >
                                        {ProvisionedThroughput.map(pt => (
                                            <option key={pt.model} value={JSON.stringify(pt)}>
                                                {pt.provider} - {pt.model}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={selectedPTCommitment}
                                        onChange={(e) => setSelectedPTCommitment(e.target.value)}
                                        className="w-full p-1 border rounded text-xs"
                                    >
                                        {selectedPTModel.commitments.map(c => (
                                            <option key={c.period} value={c.period}>
                                                {c.period} - ${c.monthlyRate}/mo
                                            </option>
                                        ))}
                                    </select>
                                </td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(ptCost.year1)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(ptCost.year2)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(ptCost.year3)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(ptCost.year4)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(ptCost.year5)}</td>
                            </tr>

                            {/* Hybrid Deployment Option */}
                            <tr className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3">
                                    <div className="font-semibold">🔄 Hybrid Deployment</div>
                                    <div className="text-[10px] text-gray-500">({hybridCost.type})</div>
                                </td>
                                <td className="py-2 px-3">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs whitespace-nowrap">On-Prem:</label>
                                            <input
                                                type="text"
                                                value={hybridOnPremPercent}
                                                onChange={(e) => {
                                                    const val = parseInt(e.target.value) || 0;
                                                    setHybridOnPremPercent(Math.min(100, Math.max(0, val)));
                                                }}
                                                className="w-16 p-1 border rounded text-xs text-right"
                                            />
                                            <span className="text-xs">%</span>
                                            <span className="text-xs text-gray-500">(Cloud: {100 - hybridOnPremPercent}%)</span>
                                        </div>
                                        <select
                                            value={hybridCloudOption}
                                            onChange={(e) => setHybridCloudOption(e.target.value)}
                                            className="w-full p-1 border rounded text-xs"
                                        >
                                            <option value="rental">GPU Rental</option>
                                            <option value="tokens">Token API</option>
                                            <option value="pt">Provisioned Throughput</option>
                                        </select>
                                    </div>
                                </td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hybridCost.year1)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hybridCost.year2)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hybridCost.year3)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hybridCost.year4)}</td>
                                <td className="text-center py-2 px-2 font-bold">{formatCurrency(hybridCost.year5)}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DeploymentCostCalculator;
